import { useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { format, parseISO } from 'date-fns';
import { clsx } from 'clsx';
import type {
  ConsumerFailedPaymentModel,
  ConsumerFailedPaymentResolutionAction,
  ConsumerFailedPaymentResolutionEventModel,
  ConsumerPartialPaymentModel,
  ConsumerPaymentRescheduleBatchModel,
  ConsumerPaymentMethodSummaryModel,
  ConsumerPaymentRescheduleModel,
  PaymentRetryStrategy,
  ReschedulePaymentPlan,
} from './failed-payments.types';
import type { PaymentAccountResourceModel } from 'api/models';
import { ApiClientContext } from 'common/contexts/api-client';
import ModalV2, { MODAL_TYPES } from 'components/Modal/ModalV2/ModalV2';
import { useFeatureFlag } from 'hooks/use-account-redesign';
import {
  createBillsPayment,
  simulateSplitPayments,
} from 'store/redux/accounts/actions';
import { selectSimulatePayments } from 'store/redux/accounts/selectors';
import { updatePaymentMethod } from 'store/redux/user/actions';
import { showToast } from 'store/redux/toast';
import { FAILED_PAYMENTS_STRINGS } from './failed-payments-strings';
import { OptionsStep } from './steps/options-step';
import { SelectHomeStep } from './steps/select-home-step';
import type {
  ActionableResolutionOption,
  FailedPaymentsAudience,
} from './failed-payments-audience';
import { getVisibleResolutionOptions } from './failed-payments-audience';
import { RetryTodaySubsection } from './subsections/retry-today-subsection';
import { ScheduleLaterSubsection } from './subsections/schedule-later-subsection';
import { ChangePaymentMethodSubsection } from './subsections/change-payment-method-subsection';
import { UnableToPaySubsection } from './subsections/unable-to-pay-subsection';
import { WithholdingSubsection } from './subsections/withholding-subsection';
import {
  DisputeChargeSubsection,
  isDisputeReasonComplete,
} from './subsections/dispute-charge-subsection';
import {
  DEDUCT_REMAINING_RIGHT_NOW,
  DeductFromRentSubsection,
  getDeductFooterCtaLabel,
} from './subsections/deduct-from-rent-subsection';
import {
  SplitItSubsection,
  SPLIT_IT_DEFAULT_INSTALLMENTS,
} from './subsections/split-it-subsection';
import { HomeownerChangePaymentMethodSubsection } from './subsections/homeowner-change-payment-method-subsection';
import { RemainingFailedPaymentsPayNowStep } from './remaining-failed-payments-pay-now-step';
import type { ResolutionSuccessModel } from './steps/resolution-success-step';
import { ResolutionSuccessStep } from './steps/resolution-success-step';
import { useDeductFromRentPreview } from './use-deduct-from-rent-preview';
import { useWireInstructions } from './use-wire-instructions';
import {
  getBalanceUniqueIds,
  getFailedPaymentErrorMessage,
  getFailedPaymentsTotal,
  getFailedPaymentsTotalForBalanceIds,
  formatAddressWithoutStateAndZip,
  formatFailedPaymentsAmount,
  formatNegativeFailedPaymentsAmount,
  getRemainingFailedPayments,
  groupFailedPaymentsByHome,
  isDisallowedRentCreditCardPayment,
  shouldUseHomeownerMultiHomeFlow,
  isFutureDueDate,
  createIdempotencyHeaders,
} from './failed-payments-utils';
import {
  useCreditCardFeeForAmount,
  useCreditCardFeeForBalanceIds,
} from './use-failed-payment-credit-card-fees';
import { usePartialPaymentAllocationPreview } from './use-failed-payment-partial-payment-preview';
import { PAY_NOW_STRINGS } from 'pages/Accounts/Pages/YourHome/Rent/PayNow/pay-now-strings';

const WITHHOLDING_REFUSAL_REASON =
  FAILED_PAYMENTS_STRINGS['withholding.fixed_reason'];

const SUCCESS_TOAST_METADATA = {
  autoDelete: true,
  autoDeleteTime: 4000,
};

const SCHEDULE_LATER_SUCCESS_DATE_FORMAT = 'EEE, MMM do';

const OPTION_TO_STRATEGY: Partial<
  Record<ActionableResolutionOption, PaymentRetryStrategy>
> = {
  retry_today: 'ScheduleForLater',
  schedule_later: 'ScheduleForLater',
  change_payment_method: 'ChangePaymentMethod',
  unable_to_pay: 'UnableToPay',
  withholding: 'RefusalToPay',
};

const OPTION_TO_EVENT_ACTION: Record<
  ActionableResolutionOption,
  ConsumerFailedPaymentResolutionAction
> = {
  retry_today: 'RetryPaymentToday',
  schedule_later: 'SchedulePayment',
  change_payment_method: 'ChangePaymentMethod',
  unable_to_pay: 'UnableToPay',
  withholding: 'Withholding',
  deduct_from_rent: 'DeductFromRent',
  split_it: 'SplitIt',
  dispute_charge: 'DisputeCharge',
};

function getScheduleLaterSuccessMessage(dateIso?: string | null) {
  const date = dateIso ? parseISO(dateIso) : null;
  const formattedDate =
    date && !Number.isNaN(date.getTime())
      ? format(date, SCHEDULE_LATER_SUCCESS_DATE_FORMAT)
      : (dateIso ?? '');

  return FAILED_PAYMENTS_STRINGS['success.toast.schedule_later'].replace(
    '{date}',
    formattedDate,
  );
}

type ResolveFailedPaymentModalProps = {
  show: boolean;
  audience: FailedPaymentsAudience;
  failedPayments: ConsumerFailedPaymentModel[];
  defaultPaymentMethod?: ConsumerPaymentMethodSummaryModel | null;
  paymentMethods: PaymentAccountResourceModel[];
  onHide: () => void;
  onResolved: () => void | Promise<void>;
};

export function ResolveFailedPaymentModal({
  show,
  audience,
  failedPayments,
  defaultPaymentMethod,
  paymentMethods,
  onHide,
  onResolved,
}: ResolveFailedPaymentModalProps) {
  const apiClient = useContext(ApiClientContext);
  const dispatch = useDispatch();

  const [selectedOption, setSelectedOption] =
    useState<ActionableResolutionOption | null>(null);
  const [selectedHomeKey, setSelectedHomeKey] = useState<string | null>(null);
  const [resolvedHomeKeys, setResolvedHomeKeys] = useState<string[]>([]);
  const [disputeFullAmount, setDisputeFullAmount] = useState<boolean | null>(
    null,
  );
  const [disputedBalanceUniqueIds, setDisputedBalanceUniqueIds] = useState<
    string[]
  >([]);
  const [disputeReasons, setDisputeReasons] = useState<Record<string, string>>(
    {},
  );
  const [disputeReasonDetails, setDisputeReasonDetails] = useState<
    Record<string, string>
  >({});
  const [deductRemainingDate, setDeductRemainingDate] = useState<string | null>(
    DEDUCT_REMAINING_RIGHT_NOW,
  );
  const [payByWireSelected, setPayByWireSelected] = useState(false);
  const [wireCoveringBalanceUniqueIds, setWireCoveringBalanceUniqueIds] =
    useState<string[]>([]);
  const [wireReceiptFile, setWireReceiptFile] = useState<File | null>(null);
  const splitItSubmitRef = useRef<(() => Promise<void>) | null>(null);
  const [splitItCanSubmit, setSplitItCanSubmit] = useState(false);
  const splitItSimulation = useSelector(selectSimulatePayments);
  const splitItMaxInstallments =
    splitItSimulation?.splitItPaymentFirst?.availableInstallmentCounts?.at(
      -1,
    ) ?? null;
  const [resolutionSuccess, setResolutionSuccess] =
    useState<ResolutionSuccessModel | null>(null);
  const [paymentAccountUniqueId, setPaymentAccountUniqueId] = useState<
    string | null
  >(null);
  const [retryDate, setRetryDate] = useState<string | null>(null);
  const [plan, setPlan] = useState<ReschedulePaymentPlan | null>(null);
  const [partialPaymentAmount, setPartialPaymentAmount] = useState('');
  const [remainingFailedPayments, setRemainingFailedPayments] = useState<
    ConsumerFailedPaymentModel[]
  >([]);
  const [
    selectedRemainingPaymentBalanceUniqueIds,
    setSelectedRemainingPaymentBalanceUniqueIds,
  ] = useState<string[]>([]);
  const [partialPaymentRemainingDate, setPartialPaymentRemainingDate] =
    useState<string | null>(null);
  const [withholdingFullPayment, setWithholdingFullPayment] = useState<
    boolean | null
  >(null);
  const [withheldBalanceUniqueIds, setWithheldBalanceUniqueIds] = useState<
    string[]
  >([]);
  const [setAsDefaultPaymentMethod, setSetAsDefaultPaymentMethod] =
    useState(false);
  const [loading, setLoading] = useState(false);
  const [lateFeePreviewError, setLateFeePreviewError] = useState(false);
  const modalContentRef = useRef<HTMLDivElement | null>(null);
  const selectedOptionSubsectionRef = useRef<HTMLDivElement | null>(null);
  const shouldScrollToSelectedOptionSubsection = useRef(false);
  const resolutionCorrelationIdRef = useRef('');
  const allHydratedFailedPayments = useMemo(
    () =>
      failedPayments.map((failedPayment) => ({
        ...failedPayment,
        paymentMethod: failedPayment.paymentMethod ?? defaultPaymentMethod,
      })),
    [defaultPaymentMethod, failedPayments],
  );
  const homeGroups = useMemo(
    () => groupFailedPaymentsByHome(allHydratedFailedPayments),
    [allHydratedFailedPayments],
  );
  const isHomeownerMultiHome = shouldUseHomeownerMultiHomeFlow(
    audience.key,
    homeGroups.length,
  );
  const hydratedFailedPayments = useMemo(() => {
    if (!isHomeownerMultiHome) return allHydratedFailedPayments;
    return (
      homeGroups.find((homeGroup) => homeGroup.key === selectedHomeKey)
        ?.failedPayments ?? []
    );
  }, [
    allHydratedFailedPayments,
    homeGroups,
    isHomeownerMultiHome,
    selectedHomeKey,
  ]);
  const activeHomeAddress =
    audience.key === 'homeowner'
      ? (hydratedFailedPayments[0]?.homeAddress ?? null)
      : null;
  const optionsStepSubtitle = isHomeownerMultiHome
    ? formatAddressWithoutStateAndZip(activeHomeAddress)
    : null;
  const { isEnabled: isSplitItEnabled } = useFeatureFlag('enable-split-it');
  const visibleResolutionOptions = useMemo(
    () =>
      getVisibleResolutionOptions(
        audience,
        hydratedFailedPayments,
        isSplitItEnabled,
      ),
    [audience, hydratedFailedPayments, isSplitItEnabled],
  );
  const balanceUniqueIds = useMemo(
    () =>
      Array.from(
        new Set(
          hydratedFailedPayments.flatMap((failedPayment) =>
            getBalanceUniqueIds(failedPayment),
          ),
        ),
      ).filter(Boolean),
    [hydratedFailedPayments],
  );
  const primaryFailedPayment = hydratedFailedPayments[0] ?? null;
  const totalAmount = getFailedPaymentsTotal(hydratedFailedPayments);
  const selectedPaymentMethod = useMemo(
    () =>
      paymentMethods.find(
        (paymentMethod) => paymentMethod.accountId === paymentAccountUniqueId,
      ) ?? null,
    [paymentAccountUniqueId, paymentMethods],
  );
  const selectedPaymentMethodForValidation = useMemo(
    () =>
      selectedPaymentMethod ??
      hydratedFailedPayments.find(
        (failedPayment) =>
          failedPayment.paymentMethod?.paymentAccountUniqueId ===
          paymentAccountUniqueId,
      )?.paymentMethod ??
      null,
    [hydratedFailedPayments, paymentAccountUniqueId, selectedPaymentMethod],
  );
  const customPartialPaymentAmount = Number(partialPaymentAmount);
  const isCustomPartialPaymentAmountInvalid =
    customPartialPaymentAmount > totalAmount;
  // Allocation/order is backend-driven: preview the split for the entered amount instead of
  // computing it client-side. Scheduled balances feed the remainder late-fee preview below.
  const {
    allocations: partialPaymentAllocations,
    loading: partialPaymentPreviewLoading,
    error: partialPaymentPreviewError,
    retry: retryPartialPaymentPreview,
  } = usePartialPaymentAllocationPreview({
    apiClient,
    amount: customPartialPaymentAmount,
    enabled:
      plan === 'ScheduledPayments' && !isCustomPartialPaymentAmountInvalid,
  });
  const partialPaymentBalances = useMemo(
    () =>
      partialPaymentAllocations
        .filter((allocation) => allocation.chargedAmount > 0)
        .map((allocation) => ({
          balanceUniqueId: allocation.balanceUniqueId,
          amount: allocation.chargedAmount,
        })),
    [partialPaymentAllocations],
  );
  const remainingPaymentBalances = useMemo(
    () =>
      partialPaymentAllocations
        .filter((allocation) => allocation.scheduledAmount > 0)
        .map((allocation) => ({
          balanceUniqueId: allocation.balanceUniqueId,
          amount: allocation.scheduledAmount,
        })),
    [partialPaymentAllocations],
  );
  const partialPaymentTotalForFee = useMemo(
    () =>
      partialPaymentBalances.reduce((sum, balance) => sum + balance.amount, 0),
    [partialPaymentBalances],
  );
  const partialPaymentRemainingTotalForFee = useMemo(
    () =>
      remainingPaymentBalances.reduce(
        (sum, balance) => sum + balance.amount,
        0,
      ),
    [remainingPaymentBalances],
  );
  const selectedRemainingPaymentTotal = useMemo(
    () =>
      getFailedPaymentsTotalForBalanceIds(
        remainingFailedPayments,
        selectedRemainingPaymentBalanceUniqueIds,
      ),
    [remainingFailedPayments, selectedRemainingPaymentBalanceUniqueIds],
  );
  const shouldPreviewActionCreditCardFee =
    selectedOption === 'retry_today' ||
    selectedOption === 'schedule_later' ||
    selectedOption === 'change_payment_method';
  const actionCreditCardFee = useCreditCardFeeForBalanceIds({
    apiClient,
    balanceUniqueIds: shouldPreviewActionCreditCardFee ? balanceUniqueIds : [],
    paymentMethod: selectedPaymentMethodForValidation,
    amount: shouldPreviewActionCreditCardFee ? totalAmount : 0,
  });
  const remainingCreditCardFee = useCreditCardFeeForBalanceIds({
    apiClient,
    balanceUniqueIds: selectedRemainingPaymentBalanceUniqueIds,
    paymentMethod: selectedPaymentMethodForValidation,
    amount: selectedRemainingPaymentTotal,
  });
  const partialPaymentCreditCardFee = useCreditCardFeeForAmount({
    apiClient,
    paymentMethod: selectedPaymentMethodForValidation,
    amount: partialPaymentTotalForFee,
  });
  const partialPaymentRemainingCreditCardFee = useCreditCardFeeForAmount({
    apiClient,
    paymentMethod: selectedPaymentMethodForValidation,
    amount: partialPaymentRemainingTotalForFee,
  });
  const isDeductFromRentSelected = selectedOption === 'deduct_from_rent';
  const canDeductFromRent =
    visibleResolutionOptions.includes('deduct_from_rent');
  const canSplitIt = visibleResolutionOptions.includes('split_it');
  const {
    preview: deductPreview,
    loading: deductPreviewLoading,
    error: deductPreviewError,
  } = useDeductFromRentPreview({
    apiClient,
    balanceUniqueIds,
    enabled: show && canDeductFromRent,
  });
  const splitItPrefetchAttemptedKeyRef = useRef<string | null>(null);
  const splitItPrefetchSucceededKeyRef = useRef<string | null>(null);
  const hasPrefetchedSplitIt =
    splitItPrefetchSucceededKeyRef.current === balanceUniqueIds.join(',');

  useEffect(() => {
    if (!show || !balanceUniqueIds.length) return;

    const splitItKey = balanceUniqueIds.join(',');
    if (!canSplitIt || splitItPrefetchAttemptedKeyRef.current === splitItKey) {
      return;
    }

    splitItPrefetchAttemptedKeyRef.current = splitItKey;

    const prefetchSplitItSimulation = async () => {
      try {
        await dispatch(
          simulateSplitPayments({
            installments: SPLIT_IT_DEFAULT_INSTALLMENTS,
            balanceUniqueIds,
          }),
        );
        splitItPrefetchSucceededKeyRef.current = splitItKey;
      } catch {
        // Best-effort: the subsection runs its own simulation whenever the
        // prefetched key does not match the selected balances.
      }
    };

    void prefetchSplitItSimulation();
  }, [show, balanceUniqueIds.join(','), canSplitIt]);
  const deductRemainingCreditCardFee = useCreditCardFeeForAmount({
    apiClient,
    paymentMethod: selectedPaymentMethodForValidation,
    amount: isDeductFromRentSelected
      ? (deductPreview?.remainingAmount ?? 0)
      : 0,
  });
  const canPayByWire =
    audience.key === 'homeowner' &&
    (selectedOption === 'change_payment_method' ||
      selectedOption === 'retry_today');
  const { instructions: wireInstructions, loading: wireInstructionsLoading } =
    useWireInstructions({
      apiClient,
      enabled: canPayByWire && payByWireSelected,
      homeUniqueId: hydratedFailedPayments[0]?.homeUniqueId,
    });

  async function recordResolutionEvent({
    action,
    stage,
    outcome,
    selectedBalanceUniqueIds = balanceUniqueIds,
    selectedPaymentMethodType,
    failureCode,
  }: {
    action: ConsumerFailedPaymentResolutionAction;
    stage: ConsumerFailedPaymentResolutionEventModel['stage'];
    outcome: ConsumerFailedPaymentResolutionEventModel['outcome'];
    selectedBalanceUniqueIds?: string[];
    selectedPaymentMethodType?: string;
    failureCode?: string;
  }) {
    const correlationId = resolutionCorrelationIdRef.current;
    if (!correlationId) return;

    const eventIdempotencyKey = `${correlationId}-${stage}-${outcome}-${action}`;
    const model: ConsumerFailedPaymentResolutionEventModel = {
      action,
      stage,
      outcome,
      balanceUniqueIds: selectedBalanceUniqueIds,
      selectedPaymentMethodType:
        selectedPaymentMethodType ??
        (payByWireSelected
          ? 'WireTransfer'
          : (selectedPaymentMethodForValidation?.paymentMethod ?? undefined)),
      correlationId,
      eventIdempotencyKey,
      failureCode,
    };

    try {
      await apiClient.post('/v2/users/failed-payments/events', model, {
        headers: { 'Idempotency-Key': eventIdempotencyKey },
      });
    } catch {}
  }

  function getCurrentEventAction(): ConsumerFailedPaymentResolutionAction | null {
    if (!selectedOption) return null;
    if (canPayByWire && payByWireSelected) {
      return 'WireTransfer';
    }
    if (selectedOption === 'unable_to_pay' && plan === 'ScheduledPayments') {
      return 'PartialPayment';
    }
    return OPTION_TO_EVENT_ACTION[selectedOption];
  }

  function recordCurrentResolutionOutcome(
    outcome: 'Succeeded' | 'Failed',
    selectedBalanceUniqueIds = balanceUniqueIds,
    failureCode?: string,
  ) {
    const action = getCurrentEventAction();
    if (!action) return;
    void recordResolutionEvent({
      action,
      stage: 'Completed',
      outcome,
      selectedBalanceUniqueIds,
      failureCode,
    });
  }

  useEffect(() => {
    if (show && allHydratedFailedPayments.length) {
      setSelectedHomeKey(null);
      setResolvedHomeKeys([]);
      setSelectedOption(null);
      setPaymentAccountUniqueId(
        allHydratedFailedPayments[0]?.paymentMethod?.paymentAccountUniqueId ??
          null,
      );
      setRetryDate(null);
      setPlan(null);
      setPartialPaymentAmount('');
      setRemainingFailedPayments([]);
      setSelectedRemainingPaymentBalanceUniqueIds([]);
      setPartialPaymentRemainingDate(null);
      setWithholdingFullPayment(null);
      setWithheldBalanceUniqueIds([]);
      setSetAsDefaultPaymentMethod(false);
      setLateFeePreviewError(false);
      resetHomeownerOptionState();
    }
  }, [show]);

  useEffect(() => {
    if (!remainingFailedPayments.length) return;

    const modalRoot = modalContentRef.current?.closest('.modal');
    if (modalRoot instanceof HTMLElement) {
      modalRoot.scrollTo({ top: 0 });
    }
  }, [remainingFailedPayments.length]);

  useEffect(() => {
    if (!selectedOption || !shouldScrollToSelectedOptionSubsection.current) {
      return;
    }

    shouldScrollToSelectedOptionSubsection.current = false;
    requestAnimationFrame(() => {
      selectedOptionSubsectionRef.current?.scrollIntoView({
        behavior: 'smooth',
        block: 'start',
      });
    });
  }, [selectedOption]);

  const strategy = useMemo<PaymentRetryStrategy | undefined>(
    () => (selectedOption ? OPTION_TO_STRATEGY[selectedOption] : undefined),
    [selectedOption],
  );

  if (!allHydratedFailedPayments.length) return null;

  const showSelectHomeStep = isHomeownerMultiHome && selectedHomeKey === null;

  function resetResolutionSelection() {
    setSelectedOption(null);
    setRetryDate(null);
    setPlan(null);
    setPartialPaymentAmount('');
    setRemainingFailedPayments([]);
    setSelectedRemainingPaymentBalanceUniqueIds([]);
    setPartialPaymentRemainingDate(null);
    setWithholdingFullPayment(null);
    setWithheldBalanceUniqueIds([]);
    setSetAsDefaultPaymentMethod(false);
    setLateFeePreviewError(false);
    resetHomeownerOptionState();
  }

  function getDisputedFailedPayments() {
    if (disputeFullAmount !== false) return hydratedFailedPayments;

    return hydratedFailedPayments.filter((failedPayment) =>
      getBalanceUniqueIds(failedPayment).some((balanceUniqueId) =>
        disputedBalanceUniqueIds.includes(balanceUniqueId),
      ),
    );
  }

  function resetHomeownerOptionState() {
    setDisputeFullAmount(null);
    setDisputedBalanceUniqueIds([]);
    setDisputeReasons({});
    setDisputeReasonDetails({});
    setDeductRemainingDate(DEDUCT_REMAINING_RIGHT_NOW);
    setPayByWireSelected(false);
    setWireCoveringBalanceUniqueIds([]);
    setWireReceiptFile(null);
    setSplitItCanSubmit(false);
    setResolutionSuccess(null);
  }

  function handleSelectHome(homeKey: string) {
    const homeGroup = homeGroups.find((group) => group.key === homeKey);
    if (!homeGroup || resolvedHomeKeys.includes(homeKey)) return;

    resetResolutionSelection();
    setPaymentAccountUniqueId(
      homeGroup.failedPayments[0]?.paymentMethod?.paymentAccountUniqueId ??
        null,
    );
    setSelectedHomeKey(homeKey);
  }

  function handleBackToHomeList() {
    resetResolutionSelection();
    setSelectedHomeKey(null);
  }

  function handleSelectOption(option: ActionableResolutionOption) {
    const optionChanged = selectedOption !== option;
    shouldScrollToSelectedOptionSubsection.current = true;
    setSelectedOption(option);

    if (optionChanged) {
      resolutionCorrelationIdRef.current =
        createIdempotencyHeaders('failed-payments-flow')['Idempotency-Key'] ??
        `failed-payments-flow-${Date.now()}`;
    }

    if (!optionChanged) {
      shouldScrollToSelectedOptionSubsection.current = false;
      requestAnimationFrame(() => {
        selectedOptionSubsectionRef.current?.scrollIntoView({
          behavior: 'smooth',
          block: 'start',
        });
      });
    }

    if (optionChanged) {
      setRetryDate(null);
      setPlan(null);
      setPartialPaymentAmount('');
      setPartialPaymentRemainingDate(null);
      setWithholdingFullPayment(null);
      setWithheldBalanceUniqueIds([]);
      setLateFeePreviewError(false);
      resetHomeownerOptionState();
    }
  }

  function handleSelectDisputeFullAmount(value: boolean) {
    setDisputeFullAmount(value);
    setDisputedBalanceUniqueIds(value ? balanceUniqueIds : []);
    if (!value) setDisputeReasons({});
  }

  function handleToggleDisputedBalanceIds(ids: string[]) {
    setDisputedBalanceUniqueIds((current) => {
      const shouldRemove = ids.every((id) => current.includes(id));
      return shouldRemove
        ? current.filter((id) => !ids.includes(id))
        : Array.from(new Set([...current, ...ids]));
    });
  }

  function handleSelectDisputeReason(balanceUniqueId: string, reason: string) {
    setDisputeReasons((current) => ({ ...current, [balanceUniqueId]: reason }));
  }

  function handleSelectPayByWire() {
    setPayByWireSelected(true);
    setWireCoveringBalanceUniqueIds(balanceUniqueIds);
    setWireReceiptFile(null);
  }

  function handleSelectWirePaymentMethod(id: string) {
    setPayByWireSelected(false);
    setWireCoveringBalanceUniqueIds([]);
    setWireReceiptFile(null);
    setPaymentAccountUniqueId(id);
  }

  function handleToggleWireCovering(ids: string[]) {
    setWireCoveringBalanceUniqueIds((current) => {
      const shouldRemove = ids.every((id) => current.includes(id));
      return shouldRemove
        ? current.filter((id) => !ids.includes(id))
        : Array.from(new Set([...current, ...ids]));
    });
  }

  function handleSelectWithholdingFullPayment(value: boolean) {
    setWithholdingFullPayment(value);
    setWithheldBalanceUniqueIds(value ? balanceUniqueIds : []);
  }

  function handleSelectUnableToPayPlan(nextPlan: ReschedulePaymentPlan) {
    setPlan(nextPlan);
    setPartialPaymentRemainingDate(null);
    setLateFeePreviewError(false);
  }

  function handleToggleRemainingPaymentBalanceIds(ids: string[]) {
    setSelectedRemainingPaymentBalanceUniqueIds((current) => {
      const shouldRemove = ids.every((id) => current.includes(id));
      return shouldRemove
        ? current.filter((id) => !ids.includes(id))
        : Array.from(new Set([...current, ...ids]));
    });
  }

  function handleToggleWithheldBalanceIds(ids: string[]) {
    setWithheldBalanceUniqueIds((current) => {
      const shouldRemove = ids.every((id) => current.includes(id));
      return shouldRemove
        ? current.filter((id) => !ids.includes(id))
        : Array.from(new Set([...current, ...ids]));
    });
  }

  async function completeActiveHomeResolution(): Promise<boolean> {
    if (isHomeownerMultiHome && selectedHomeKey !== null) {
      const nextResolvedHomeKeys = resolvedHomeKeys.includes(selectedHomeKey)
        ? resolvedHomeKeys
        : [...resolvedHomeKeys, selectedHomeKey];

      if (nextResolvedHomeKeys.length < homeGroups.length) {
        setResolvedHomeKeys(nextResolvedHomeKeys);
        resetResolutionSelection();
        setSelectedHomeKey(null);
        return true;
      }
    }

    await onResolved();
    return false;
  }

  async function showSuccessToast(
    message = FAILED_PAYMENTS_STRINGS['success.toast'],
  ) {
    await completeActiveHomeResolution();
    dispatch(showToast({ message, created: true }, SUCCESS_TOAST_METADATA));
  }

  function showActionResolvedToast(message: string) {
    dispatch(showToast({ message, created: true }, SUCCESS_TOAST_METADATA));
  }

  function showErrorToast(message: string) {
    dispatch(
      showToast(
        { message, error: true },
        { autoDelete: true, autoDeleteTime: 5000 },
      ),
    );
  }

  async function handleHide() {
    if (remainingFailedPayments.length) {
      setRemainingFailedPayments([]);
      setSelectedRemainingPaymentBalanceUniqueIds([]);
      await onResolved();
      return;
    }

    if (resolvedHomeKeys.length) {
      await onResolved();
      return;
    }

    onHide();
  }

  function showRemainingPayNowStep(
    resolvedBalanceUniqueIds: string[],
    successMessage?: string,
  ) {
    const nextRemainingFailedPayments = getRemainingFailedPayments(
      hydratedFailedPayments,
      resolvedBalanceUniqueIds,
    );

    if (!nextRemainingFailedPayments.length) return false;

    setRemainingFailedPayments(nextRemainingFailedPayments);
    setSelectedRemainingPaymentBalanceUniqueIds([]);
    if (successMessage) {
      showActionResolvedToast(successMessage);
    }

    return true;
  }

  async function updateDefaultPaymentMethodIfRequested(
    nextPaymentAccountUniqueId?: string | null,
  ) {
    if (!setAsDefaultPaymentMethod || !nextPaymentAccountUniqueId) return;

    try {
      await dispatch(
        updatePaymentMethod(
          nextPaymentAccountUniqueId,
          audience.paymentPreference,
          'Primary',
        ),
      );
    } catch {
      showErrorToast(
        FAILED_PAYMENTS_STRINGS['error.default_payment_method_update'],
      );
    }
  }

  function blockDisallowedRentCreditCardIfNecessary(
    selectedBalanceUniqueIds: string[],
  ) {
    if (
      isDisallowedRentCreditCardPayment({
        failedPayments: hydratedFailedPayments,
        balanceUniqueIds: selectedBalanceUniqueIds,
        paymentMethod: selectedPaymentMethodForValidation,
      })
    ) {
      showErrorToast(
        FAILED_PAYMENTS_STRINGS['error.rent_credit_card_not_allowed'],
      );
      return true;
    }
    return false;
  }

  async function submitReschedule() {
    if (!strategy) return;
    const isRetryToday = selectedOption === 'retry_today';
    const retryTodayDate = format(new Date(), 'yyyy-MM-dd');
    const isWithholding = strategy === 'RefusalToPay';
    const rescheduleBalanceUniqueIds = isWithholding
      ? withholdingFullPayment === false
        ? withheldBalanceUniqueIds
        : balanceUniqueIds
      : balanceUniqueIds;

    const model: ConsumerPaymentRescheduleModel = {
      retryStrategy: strategy,
      paymentAccountUniqueId:
        strategy === 'ScheduleForLater' || strategy === 'ChangePaymentMethod'
          ? (paymentAccountUniqueId ?? undefined)
          : undefined,
      scheduledRetryDate:
        strategy === 'ScheduleForLater'
          ? isRetryToday
            ? retryTodayDate
            : (retryDate ?? undefined)
          : undefined,
      paymentPlan: strategy === 'UnableToPay' ? (plan ?? undefined) : undefined,
      refusalReason:
        strategy === 'RefusalToPay' ? WITHHOLDING_REFUSAL_REASON : undefined,
    };

    if (
      (strategy === 'ScheduleForLater' || strategy === 'ChangePaymentMethod') &&
      !model.paymentAccountUniqueId
    ) {
      showErrorToast(FAILED_PAYMENTS_STRINGS['error.missing_payment_method']);
      return;
    }
    if (
      strategy === 'ScheduleForLater' &&
      !isRetryToday &&
      !model.scheduledRetryDate
    ) {
      showErrorToast(FAILED_PAYMENTS_STRINGS['error.missing_date']);
      return;
    }
    if (
      strategy === 'ScheduleForLater' &&
      !isRetryToday &&
      lateFeePreviewError
    ) {
      showErrorToast(FAILED_PAYMENTS_STRINGS['error.late_fee_preview']);
      return;
    }
    if (strategy === 'UnableToPay' && !model.paymentPlan) {
      showErrorToast(FAILED_PAYMENTS_STRINGS['error.missing_plan']);
      return;
    }
    if (isWithholding && withholdingFullPayment == null) {
      showErrorToast(
        FAILED_PAYMENTS_STRINGS['error.missing_withholding_choice'],
      );
      return;
    }
    if (isWithholding && rescheduleBalanceUniqueIds.length === 0) {
      showErrorToast(FAILED_PAYMENTS_STRINGS['error.missing_withheld_balance']);
      return;
    }
    if (getNonSelfResolvableFailedPayments(rescheduleBalanceUniqueIds).length) {
      showErrorToast(FAILED_PAYMENTS_STRINGS['error.unresolvable_balances']);
      return;
    }
    if (
      !isWithholding &&
      blockDisallowedRentCreditCardIfNecessary(rescheduleBalanceUniqueIds)
    ) {
      return;
    }

    setLoading(true);
    try {
      await submitPaymentRescheduleBatch(rescheduleBalanceUniqueIds, model);
      await updateDefaultPaymentMethodIfRequested(model.paymentAccountUniqueId);
      recordCurrentResolutionOutcome('Succeeded', rescheduleBalanceUniqueIds);
      const successMessage = isRetryToday
        ? FAILED_PAYMENTS_STRINGS['success.toast.retry_today']
        : selectedOption === 'schedule_later'
          ? getScheduleLaterSuccessMessage(model.scheduledRetryDate)
          : isWithholding
            ? FAILED_PAYMENTS_STRINGS['success.toast.withholding']
            : FAILED_PAYMENTS_STRINGS['success.toast.hold'];

      if (showRemainingPayNowStep(rescheduleBalanceUniqueIds, successMessage)) {
        return;
      }
      await showSuccessToast(successMessage);
    } catch (e: unknown) {
      recordCurrentResolutionOutcome(
        'Failed',
        rescheduleBalanceUniqueIds,
        e instanceof Error ? e.name : 'UnknownError',
      );
      const message = getFailedPaymentErrorMessage(
        e,
        PAY_NOW_STRINGS['payment.error'],
      );
      showErrorToast(message);
    } finally {
      setLoading(false);
    }
  }

  async function submitChangePaymentMethodPayment() {
    if (!paymentAccountUniqueId) {
      showErrorToast(FAILED_PAYMENTS_STRINGS['error.missing_payment_method']);
      return;
    }
    if (blockDisallowedRentCreditCardIfNecessary(balanceUniqueIds)) {
      return;
    }

    setLoading(true);
    try {
      await dispatch(
        createBillsPayment(
          {
            paymentAccountUniqueId,
            balances: balanceUniqueIds,
          },
          {
            headers: createIdempotencyHeaders('failed-payments-bills-payment'),
          },
        ),
      );

      await updateDefaultPaymentMethodIfRequested(paymentAccountUniqueId);
      recordCurrentResolutionOutcome('Succeeded');
      const successMessage =
        FAILED_PAYMENTS_STRINGS['success.toast.change_payment_method'];
      if (showRemainingPayNowStep(balanceUniqueIds, successMessage)) {
        return;
      }
      await showSuccessToast(successMessage);
    } catch (e: unknown) {
      recordCurrentResolutionOutcome(
        'Failed',
        balanceUniqueIds,
        e instanceof Error ? e.name : 'UnknownError',
      );
      const message = getFailedPaymentErrorMessage(
        e,
        FAILED_PAYMENTS_STRINGS['error.generic'],
      );
      showErrorToast(message);
    } finally {
      setLoading(false);
    }
  }

  async function submitRemainingPayment() {
    if (!paymentAccountUniqueId) {
      showErrorToast(FAILED_PAYMENTS_STRINGS['error.missing_payment_method']);
      return;
    }
    if (selectedRemainingPaymentBalanceUniqueIds.length === 0) {
      showErrorToast(FAILED_PAYMENTS_STRINGS['error.missing_selected_balance']);
      return;
    }
    if (
      blockDisallowedRentCreditCardIfNecessary(
        selectedRemainingPaymentBalanceUniqueIds,
      )
    ) {
      return;
    }

    setLoading(true);
    try {
      await dispatch(
        createBillsPayment(
          {
            paymentAccountUniqueId,
            balances: selectedRemainingPaymentBalanceUniqueIds,
          },
          {
            headers: createIdempotencyHeaders(
              'failed-payments-remaining-bills-payment',
            ),
          },
        ),
      );

      await showSuccessToast(PAY_NOW_STRINGS['payment.success']);
    } catch (e: unknown) {
      const message = getFailedPaymentErrorMessage(
        e,
        PAY_NOW_STRINGS['payment.error'],
      );
      showErrorToast(message);
    } finally {
      setLoading(false);
    }
  }

  async function submitPartialPayment() {
    if (!paymentAccountUniqueId) {
      showErrorToast(FAILED_PAYMENTS_STRINGS['error.missing_payment_method']);
      return;
    }

    const partialAmount = Number(partialPaymentAmount);
    if (!Number.isFinite(partialAmount) || partialAmount <= 0) {
      showErrorToast(FAILED_PAYMENTS_STRINGS['error.missing_amount']);
      return;
    }
    if (isCustomPartialPaymentAmountInvalid) {
      showErrorToast(FAILED_PAYMENTS_STRINGS['error.amount_too_high']);
      return;
    }
    if (
      partialPaymentPreviewLoading ||
      partialPaymentPreviewError ||
      !partialPaymentBalances.length ||
      !remainingPaymentBalances.length
    ) {
      showErrorToast(FAILED_PAYMENTS_STRINGS['error.partial_payment_preview']);
      return;
    }

    if (!partialPaymentRemainingDate) {
      showErrorToast(FAILED_PAYMENTS_STRINGS['error.missing_date']);
      return;
    }
    if (lateFeePreviewError) {
      showErrorToast(FAILED_PAYMENTS_STRINGS['error.late_fee_preview']);
      return;
    }
    if (blockDisallowedRentCreditCardIfNecessary(balanceUniqueIds)) {
      return;
    }

    setLoading(true);
    try {
      // Backend decides the allocation across balances; the client sends only the amount and the
      // date for any unpaid remainder.
      const body: ConsumerPartialPaymentModel = {
        paymentAccountUniqueId,
        amount: partialAmount,
        remainingProcessOn: partialPaymentRemainingDate,
      };

      await apiClient.post('/v2/users/failed-payments/payments/partial', body, {
        headers: createIdempotencyHeaders('failed-payments-partial'),
      });
      recordCurrentResolutionOutcome('Succeeded');

      await showSuccessToast(
        FAILED_PAYMENTS_STRINGS['success.toast.partial_payment'],
      );
    } catch (e: unknown) {
      recordCurrentResolutionOutcome(
        'Failed',
        balanceUniqueIds,
        e instanceof Error ? e.name : 'UnknownError',
      );
      const message = getFailedPaymentErrorMessage(
        e,
        PAY_NOW_STRINGS['payment.error'],
      );
      showErrorToast(message);
    } finally {
      setLoading(false);
    }
  }

  async function submitDispute() {
    if (disputeFullAmount == null) {
      showErrorToast(FAILED_PAYMENTS_STRINGS['dispute.error.missing_choice']);
      return;
    }
    const disputeBalanceUniqueIds = disputeFullAmount
      ? balanceUniqueIds
      : disputedBalanceUniqueIds;
    if (disputeBalanceUniqueIds.length === 0) {
      showErrorToast(FAILED_PAYMENTS_STRINGS['dispute.error.missing_balance']);
      return;
    }

    const submittedDisputeReasons: {
      balanceUniqueId: string;
      reasonCode: string;
      note?: string;
    }[] = [];
    for (const failedPayment of getDisputedFailedPayments()) {
      const balanceUniqueId = getBalanceUniqueIds(failedPayment)[0];
      if (!balanceUniqueId) continue;
      const reasonCode = disputeReasons[balanceUniqueId];
      if (!reasonCode) {
        showErrorToast(FAILED_PAYMENTS_STRINGS['dispute.error.missing_reason']);
        return;
      }
      const note = (disputeReasonDetails[balanceUniqueId] ?? '').trim();
      if (!isDisputeReasonComplete(failedPayment, reasonCode, note)) {
        showErrorToast(
          FAILED_PAYMENTS_STRINGS['dispute.error.missing_reason_detail'],
        );
        return;
      }
      submittedDisputeReasons.push({
        balanceUniqueId,
        reasonCode,
        note: note || undefined,
      });
    }

    if (getNonSelfResolvableFailedPayments(disputeBalanceUniqueIds).length) {
      showErrorToast(FAILED_PAYMENTS_STRINGS['error.unresolvable_balances']);
      return;
    }

    setLoading(true);
    try {
      await submitPaymentRescheduleBatch(disputeBalanceUniqueIds, {
        retryStrategy: 'RefusalToPay',
        disputeReasons: submittedDisputeReasons,
      });
      recordCurrentResolutionOutcome('Succeeded', disputeBalanceUniqueIds);
      await showSuccessToast(FAILED_PAYMENTS_STRINGS['success.toast.dispute']);
    } catch (e: unknown) {
      recordCurrentResolutionOutcome(
        'Failed',
        disputeBalanceUniqueIds,
        e instanceof Error ? e.name : 'UnknownError',
      );
      showErrorToast(
        getFailedPaymentErrorMessage(
          e,
          FAILED_PAYMENTS_STRINGS['error.generic'],
        ),
      );
    } finally {
      setLoading(false);
    }
  }

  async function submitDeductFromRent() {
    if (!deductPreview) {
      showErrorToast(FAILED_PAYMENTS_STRINGS['deduct.error.preview']);
      return;
    }
    const hasRemainder = deductPreview.remainingAmount > 0;
    if (hasRemainder && !deductRemainingDate) {
      showErrorToast(FAILED_PAYMENTS_STRINGS['error.missing_date']);
      return;
    }
    if (hasRemainder && !paymentAccountUniqueId) {
      showErrorToast(FAILED_PAYMENTS_STRINGS['error.missing_payment_method']);
      return;
    }

    setLoading(true);
    try {
      await apiClient.post(
        '/v2/users/failed-payments/deduct-from-rent',
        {
          balanceUniqueIds,
          remainingProcessOn: hasRemainder
            ? deductRemainingDate === DEDUCT_REMAINING_RIGHT_NOW
              ? null
              : deductRemainingDate
            : undefined,
          paymentAccountUniqueId: hasRemainder
            ? paymentAccountUniqueId
            : undefined,
        },
        { headers: createIdempotencyHeaders('failed-payments-deduct') },
      );
      recordCurrentResolutionOutcome('Succeeded');
      await showSuccessToast(
        FAILED_PAYMENTS_STRINGS['success.toast.deduct_from_rent'],
      );
    } catch (e: unknown) {
      recordCurrentResolutionOutcome(
        'Failed',
        balanceUniqueIds,
        e instanceof Error ? e.name : 'UnknownError',
      );
      showErrorToast(
        getFailedPaymentErrorMessage(
          e,
          FAILED_PAYMENTS_STRINGS['error.generic'],
        ),
      );
    } finally {
      setLoading(false);
    }
  }

  async function submitWireReceipt() {
    if (!wireReceiptFile) {
      showErrorToast(FAILED_PAYMENTS_STRINGS['wire.error.missing_receipt']);
      return;
    }
    if (wireCoveringBalanceUniqueIds.length === 0) {
      showErrorToast(FAILED_PAYMENTS_STRINGS['wire.error.missing_covering']);
      return;
    }

    setLoading(true);
    try {
      const body = new FormData();
      body.append('receipt', wireReceiptFile);
      for (const balanceUniqueId of wireCoveringBalanceUniqueIds) {
        body.append('balanceUniqueIds', balanceUniqueId);
      }
      await apiClient.post('/v2/users/failed-payments/wire-receipts', body, {
        headers: createIdempotencyHeaders('failed-payments-wire-receipt'),
      });
      recordCurrentResolutionOutcome('Succeeded', wireCoveringBalanceUniqueIds);

      const coveredFailedPayments = hydratedFailedPayments.filter(
        (failedPayment) =>
          getBalanceUniqueIds(failedPayment).some((balanceUniqueId) =>
            wireCoveringBalanceUniqueIds.includes(balanceUniqueId),
          ),
      );
      setResolutionSuccess({
        title: FAILED_PAYMENTS_STRINGS['wire.success.title'],
        subtitle: FAILED_PAYMENTS_STRINGS['wire.success.subtitle'],
        reference: wireInstructions
          ? FAILED_PAYMENTS_STRINGS['wire.success.reference'].replace(
              '{code}',
              wireInstructions.referenceCode,
            )
          : undefined,
        rows: coveredFailedPayments.map((failedPayment) => ({
          key:
            getBalanceUniqueIds(failedPayment)[0] ?? failedPayment.label ?? '',
          label: failedPayment.label ?? '',
          status: FAILED_PAYMENTS_STRINGS[
            'wire.success.pending_review'
          ].replace('{home}', failedPayment.homeAddress ?? ''),
          statusColorClassName: 'text-orange',
          amount: failedPayment.amount ?? 0,
        })),
        totalAmount: getFailedPaymentsTotal(coveredFailedPayments),
      });
    } catch (e: unknown) {
      recordCurrentResolutionOutcome(
        'Failed',
        wireCoveringBalanceUniqueIds,
        e instanceof Error ? e.name : 'UnknownError',
      );
      showErrorToast(
        getFailedPaymentErrorMessage(
          e,
          FAILED_PAYMENTS_STRINGS['error.generic'],
        ),
      );
    } finally {
      setLoading(false);
    }
  }

  function handleSplitItSuccess({
    installments,
    monthlyPayment,
    upfrontAmount,
  }: {
    installments: number;
    monthlyPayment: number;
    upfrontAmount: number;
  }) {
    recordCurrentResolutionOutcome('Succeeded');
    const firstChargeDate = format(
      new Date(new Date().getFullYear(), new Date().getMonth() + 1, 1),
      'MMM d',
    );
    const noteBase = FAILED_PAYMENTS_STRINGS['split.success.note']
      .replace(
        '{installment}',
        formatNegativeFailedPaymentsAmount(monthlyPayment).replace('-', ''),
      )
      .replace('{date}', firstChargeDate)
      .replace('{count}', String(installments));
    const firstNote =
      upfrontAmount > 0
        ? FAILED_PAYMENTS_STRINGS['split.success.first_note']
            .replace(
              '{upfront}',
              formatNegativeFailedPaymentsAmount(upfrontAmount).replace(
                '-',
                '',
              ),
            )
            .replace(
              '{installment}',
              formatNegativeFailedPaymentsAmount(monthlyPayment).replace(
                '-',
                '',
              ),
            )
            .replace('{date}', firstChargeDate)
            .replace('{count}', String(installments))
        : noteBase;

    setResolutionSuccess({
      title: FAILED_PAYMENTS_STRINGS['split.success.title'],
      rows: hydratedFailedPayments.map((failedPayment, index) => ({
        key: getBalanceUniqueIds(failedPayment)[0] ?? failedPayment.label ?? '',
        label: failedPayment.label ?? '',
        status: FAILED_PAYMENTS_STRINGS['split.success.on_plan'],
        statusColorClassName: 'text-blue',
        amount: failedPayment.amount ?? 0,
        note: index === 0 ? firstNote : noteBase,
      })),
    });
  }

  function handleSplitItFailure() {
    recordCurrentResolutionOutcome('Failed', balanceUniqueIds, 'SplitItError');
  }

  async function submitPaymentRescheduleBatch(
    selectedBalanceUniqueIds: string[],
    model: ConsumerPaymentRescheduleModel,
  ) {
    const body: ConsumerPaymentRescheduleBatchModel = {
      balanceUniqueIds: selectedBalanceUniqueIds,
      paymentReschedule: model,
    };

    return apiClient.post(
      '/v2/users/failed-payments/payment-reschedules',
      body,
      {
        headers: createIdempotencyHeaders('failed-payments-reschedule'),
      },
    );
  }

  function getNonSelfResolvableFailedPayments(
    selectedBalanceUniqueIds: string[],
  ) {
    return hydratedFailedPayments.filter(
      (failedPayment) =>
        !failedPayment.canSelfResolve &&
        getBalanceUniqueIds(failedPayment).some((balanceUniqueId) =>
          selectedBalanceUniqueIds.includes(balanceUniqueId),
        ),
    );
  }

  const stepContent = (() => {
    if (resolutionSuccess) {
      return (
        <ResolutionSuccessStep
          success={resolutionSuccess}
          onDone={completeActiveHomeResolution}
        />
      );
    }

    if (showSelectHomeStep) {
      return (
        <SelectHomeStep
          homeGroups={homeGroups}
          resolvedHomeKeys={resolvedHomeKeys}
          onSelectHome={handleSelectHome}
        />
      );
    }

    if (!primaryFailedPayment || balanceUniqueIds.length === 0) return null;
    const fp: ConsumerFailedPaymentModel = primaryFailedPayment;

    if (remainingFailedPayments.length) {
      return (
        <RemainingFailedPaymentsPayNowStep
          failedPayments={remainingFailedPayments}
          selectedBalanceUniqueIds={selectedRemainingPaymentBalanceUniqueIds}
          paymentMethods={paymentMethods}
          selectedPaymentAccountUniqueId={paymentAccountUniqueId}
          paymentMethod={
            remainingFailedPayments[0]?.paymentMethod ?? fp.paymentMethod
          }
          creditCardFeeAmount={remainingCreditCardFee?.feeAmount}
          submitDisabled={
            !paymentAccountUniqueId ||
            !selectedRemainingPaymentBalanceUniqueIds.length
          }
          submitLoading={loading}
          onToggleBalanceIds={handleToggleRemainingPaymentBalanceIds}
          onSelectPaymentMethod={setPaymentAccountUniqueId}
          onSubmit={submitRemainingPayment}
        />
      );
    }

    const isRetryTodayInline = selectedOption === 'retry_today';
    const isScheduleLaterInline = selectedOption === 'schedule_later';
    const isChangePaymentMethodInline =
      selectedOption === 'change_payment_method';
    const isUnableToPayInline = selectedOption === 'unable_to_pay';
    const isWithholdingInline = selectedOption === 'withholding';
    const isDisputeInline = selectedOption === 'dispute_charge';
    const isDeductInline = selectedOption === 'deduct_from_rent';
    const isSplitItInline = selectedOption === 'split_it';
    const isWirePayment = canPayByWire && payByWireSelected;
    const deductHasRemainder = (deductPreview?.remainingAmount ?? 0) > 0;
    const selectedActionTotalWithFee =
      actionCreditCardFee?.totalWithFee ?? totalAmount;
    const withholdingSelectedFuturePayment =
      withholdingFullPayment === false &&
      hydratedFailedPayments.some(
        (failedPayment) =>
          getBalanceUniqueIds(failedPayment).some((balanceUniqueId) =>
            withheldBalanceUniqueIds.includes(balanceUniqueId),
          ) && isFutureDueDate(failedPayment.dueOn),
      );
    const subsection = isRetryTodayInline ? (
      <RetryTodaySubsection
        failedPayments={hydratedFailedPayments}
        paymentMethods={paymentMethods}
        paymentAccountUniqueId={paymentAccountUniqueId}
        paymentMethod={fp.paymentMethod}
        onSelectPaymentMethod={handleSelectWirePaymentMethod}
        canPayByWire={canPayByWire}
        payByWireSelected={payByWireSelected}
        onSelectPayByWire={handleSelectPayByWire}
        wireInstructions={wireInstructions}
        wireInstructionsLoading={wireInstructionsLoading}
        wireCoveringBalanceUniqueIds={wireCoveringBalanceUniqueIds}
        onToggleWireCovering={handleToggleWireCovering}
        wireReceiptFile={wireReceiptFile}
        onWireReceiptChange={setWireReceiptFile}
      />
    ) : isScheduleLaterInline ? (
      <ScheduleLaterSubsection
        apiClient={apiClient}
        audience={audience}
        balanceUniqueIds={balanceUniqueIds}
        paymentMethods={paymentMethods}
        paymentAccountUniqueId={paymentAccountUniqueId}
        paymentMethod={fp.paymentMethod}
        selectedDate={retryDate}
        onSelectDate={setRetryDate}
        onSelectPaymentMethod={setPaymentAccountUniqueId}
        onLateFeeErrorChange={setLateFeePreviewError}
      />
    ) : isChangePaymentMethodInline ? (
      audience.key === 'homeowner' ? (
        <HomeownerChangePaymentMethodSubsection
          failedPayments={hydratedFailedPayments}
          paymentMethods={paymentMethods}
          selectedPaymentAccountUniqueId={paymentAccountUniqueId}
          payByWireSelected={payByWireSelected}
          onSelectPaymentMethod={handleSelectWirePaymentMethod}
          onSelectPayByWire={handleSelectPayByWire}
          wireInstructions={wireInstructions}
          wireInstructionsLoading={wireInstructionsLoading}
          wireCoveringBalanceUniqueIds={wireCoveringBalanceUniqueIds}
          onToggleWireCovering={handleToggleWireCovering}
          wireReceiptFile={wireReceiptFile}
          onWireReceiptChange={setWireReceiptFile}
        />
      ) : (
        <ChangePaymentMethodSubsection
          paymentMethods={paymentMethods}
          selectedPaymentAccountUniqueId={paymentAccountUniqueId}
          setAsDefault={setAsDefaultPaymentMethod}
          onSelectPaymentMethod={setPaymentAccountUniqueId}
          onSetAsDefaultChange={setSetAsDefaultPaymentMethod}
        />
      )
    ) : isUnableToPayInline ? (
      <UnableToPaySubsection
        apiClient={apiClient}
        paymentMethods={paymentMethods}
        paymentAccountUniqueId={paymentAccountUniqueId}
        paymentMethod={fp.paymentMethod}
        selectedPlan={plan}
        partialPaymentAmount={partialPaymentAmount}
        creditCardFeeAmount={partialPaymentCreditCardFee?.feeAmount}
        remainingCreditCardFeeAmount={
          partialPaymentRemainingCreditCardFee?.feeAmount
        }
        isPartialPaymentAmountInvalid={isCustomPartialPaymentAmountInvalid}
        isPartialPaymentPreviewLoading={partialPaymentPreviewLoading}
        partialPaymentPreviewError={partialPaymentPreviewError}
        remainingPaymentDate={partialPaymentRemainingDate}
        remainingPaymentBalances={remainingPaymentBalances}
        onSelectPlan={handleSelectUnableToPayPlan}
        onChangePartialPaymentAmount={setPartialPaymentAmount}
        onSelectRemainingPaymentDate={setPartialPaymentRemainingDate}
        onSelectPaymentMethod={setPaymentAccountUniqueId}
        onLateFeeErrorChange={setLateFeePreviewError}
        onRetryPartialPaymentPreview={retryPartialPaymentPreview}
      />
    ) : isWithholdingInline ? (
      <WithholdingSubsection
        failedPayments={hydratedFailedPayments}
        withholdingFullPayment={withholdingFullPayment}
        withheldBalanceUniqueIds={withheldBalanceUniqueIds}
        onSelectFullPayment={handleSelectWithholdingFullPayment}
        onToggleBalanceIds={handleToggleWithheldBalanceIds}
      />
    ) : isDisputeInline ? (
      <DisputeChargeSubsection
        failedPayments={hydratedFailedPayments}
        disputeFullAmount={disputeFullAmount}
        disputedBalanceUniqueIds={disputedBalanceUniqueIds}
        disputeReasons={disputeReasons}
        disputeReasonDetails={disputeReasonDetails}
        onChangeReasonDetail={(balanceUniqueId, detail) =>
          setDisputeReasonDetails((current) => ({
            ...current,
            [balanceUniqueId]: detail,
          }))
        }
        onSelectFullAmount={handleSelectDisputeFullAmount}
        onToggleBalanceIds={handleToggleDisputedBalanceIds}
        onSelectReason={handleSelectDisputeReason}
      />
    ) : isDeductInline ? (
      <DeductFromRentSubsection
        apiClient={apiClient}
        homeAddress={activeHomeAddress}
        failedPayments={hydratedFailedPayments}
        balanceUniqueIds={balanceUniqueIds}
        preview={deductPreview}
        previewLoading={deductPreviewLoading}
        previewError={deductPreviewError}
        paymentMethods={paymentMethods}
        paymentAccountUniqueId={paymentAccountUniqueId}
        paymentMethod={fp.paymentMethod}
        creditCardFeeAmount={deductRemainingCreditCardFee?.feeAmount}
        selectedRemainingDate={deductRemainingDate}
        onSelectRemainingDate={setDeductRemainingDate}
        onSelectPaymentMethod={setPaymentAccountUniqueId}
      />
    ) : isSplitItInline ? (
      <SplitItSubsection
        hasPrefetchedSimulation={hasPrefetchedSplitIt}
        balanceUniqueIds={balanceUniqueIds}
        totalAmount={totalAmount}
        upfrontPaymentAccountUniqueId={paymentAccountUniqueId}
        onCanSubmitChange={setSplitItCanSubmit}
        registerSubmit={(submit) => {
          splitItSubmitRef.current = submit;
        }}
        onSuccess={handleSplitItSuccess}
        onFailure={handleSplitItFailure}
        onLoadingChange={setLoading}
      />
    ) : null;
    const submitDisabled = isWirePayment
      ? !wireReceiptFile || wireCoveringBalanceUniqueIds.length === 0
      : isRetryTodayInline
        ? !paymentAccountUniqueId
        : isScheduleLaterInline
          ? !retryDate || !paymentAccountUniqueId || lateFeePreviewError
          : isDisputeInline
            ? disputeFullAmount == null ||
              (disputeFullAmount === false &&
                disputedBalanceUniqueIds.length === 0) ||
              getDisputedFailedPayments().some((failedPayment) => {
                const balanceUniqueId = getBalanceUniqueIds(failedPayment)[0];
                if (!balanceUniqueId) return false;
                return !isDisputeReasonComplete(
                  failedPayment,
                  disputeReasons[balanceUniqueId],
                  disputeReasonDetails[balanceUniqueId],
                );
              })
            : isDeductInline
              ? !deductPreview ||
                deductPreviewLoading ||
                deductPreviewError ||
                (deductHasRemainder &&
                  (!deductRemainingDate || !paymentAccountUniqueId))
              : isSplitItInline
                ? !splitItCanSubmit
                : isChangePaymentMethodInline
                  ? !paymentAccountUniqueId
                  : isUnableToPayInline
                    ? !plan ||
                      (plan === 'ScheduledPayments' &&
                        (!paymentAccountUniqueId ||
                          !partialPaymentRemainingDate ||
                          lateFeePreviewError ||
                          partialPaymentPreviewLoading ||
                          partialPaymentPreviewError ||
                          !partialPaymentBalances.length ||
                          !remainingPaymentBalances.length ||
                          isCustomPartialPaymentAmountInvalid ||
                          !Number(partialPaymentAmount)))
                    : isWithholdingInline
                      ? withholdingFullPayment == null ||
                        (withholdingFullPayment === false &&
                          withheldBalanceUniqueIds.length === 0)
                      : !selectedOption;
    const isHomeowner = audience.key === 'homeowner';
    const disputeSubmitLabel =
      disputeFullAmount === false
        ? FAILED_PAYMENTS_STRINGS['dispute.footer_cta_partial'].replace(
            '{amount}',
            formatFailedPaymentsAmount(
              Math.abs(
                getFailedPaymentsTotalForBalanceIds(
                  hydratedFailedPayments,
                  disputedBalanceUniqueIds,
                ),
              ),
            ),
          )
        : FAILED_PAYMENTS_STRINGS['dispute.footer_cta_full'];
    const deductSubmitLabel = deductPreview
      ? getDeductFooterCtaLabel(
          deductPreview,
          (deductPreview.remainingAmount ?? 0) +
            (deductRemainingCreditCardFee?.feeAmount ?? 0),
        )
      : FAILED_PAYMENTS_STRINGS['modal.footer.confirm'];
    const submitLabel = isWirePayment
      ? FAILED_PAYMENTS_STRINGS['wire.submit']
      : isRetryTodayInline
        ? `Retry Now | ${formatNegativeFailedPaymentsAmount(
            selectedActionTotalWithFee,
          )}`
        : isScheduleLaterInline
          ? `Schedule Now | ${formatNegativeFailedPaymentsAmount(
              selectedActionTotalWithFee,
            )}`
          : isDisputeInline
            ? disputeSubmitLabel
            : isDeductInline
              ? deductSubmitLabel
              : isSplitItInline
                ? FAILED_PAYMENTS_STRINGS['split.footer_cta']
                : isChangePaymentMethodInline
                  ? isHomeowner
                    ? FAILED_PAYMENTS_STRINGS['modal.footer.submit']
                    : `Pay Now | ${formatNegativeFailedPaymentsAmount(
                        selectedActionTotalWithFee,
                      )}`
                  : isUnableToPayInline
                    ? plan === 'ScheduledPayments'
                      ? FAILED_PAYMENTS_STRINGS['modal.footer.confirm_and_pay']
                      : FAILED_PAYMENTS_STRINGS['modal.footer.cta']
                    : isWithholdingInline
                      ? withholdingSelectedFuturePayment
                        ? FAILED_PAYMENTS_STRINGS[
                            'modal.footer.confirm_payment'
                          ]
                        : FAILED_PAYMENTS_STRINGS['modal.footer.done']
                      : isHomeowner
                        ? FAILED_PAYMENTS_STRINGS['modal.footer.next']
                        : FAILED_PAYMENTS_STRINGS['modal.footer.cta'];
    return (
      <OptionsStep
        audience={audience}
        failedPayments={hydratedFailedPayments}
        options={visibleResolutionOptions}
        subtitle={optionsStepSubtitle}
        optionSubtitles={{
          split_it: splitItMaxInstallments
            ? FAILED_PAYMENTS_STRINGS[
                'option.homeowner.split_it_subtitle'
              ].replace('{count}', String(splitItMaxInstallments))
            : null,
        }}
        selectedOption={selectedOption}
        onSelectOption={handleSelectOption}
        onSubmit={
          isWirePayment
            ? submitWireReceipt
            : isRetryTodayInline ||
                isScheduleLaterInline ||
                (isUnableToPayInline && plan !== 'ScheduledPayments') ||
                isWithholdingInline
              ? submitReschedule
              : isUnableToPayInline && plan === 'ScheduledPayments'
                ? submitPartialPayment
                : isDisputeInline
                  ? submitDispute
                  : isDeductInline
                    ? submitDeductFromRent
                    : isSplitItInline
                      ? () => splitItSubmitRef.current?.()
                      : isChangePaymentMethodInline
                        ? submitChangePaymentMethodPayment
                        : () => {}
        }
        submitLabel={submitLabel}
        submitDisabled={submitDisabled}
        submitLoading={loading}
        wideSubmit={
          isRetryTodayInline ||
          isScheduleLaterInline ||
          isChangePaymentMethodInline ||
          (isUnableToPayInline && plan === 'ScheduledPayments') ||
          (isDeductInline && deductHasRemainder) ||
          withholdingSelectedFuturePayment
        }
        subsection={
          subsection ? (
            <div ref={selectedOptionSubsectionRef}>{subsection}</div>
          ) : null
        }
      />
    );
  })();

  return (
    <ModalV2
      show={show}
      onHide={handleHide}
      type={MODAL_TYPES.STANDARD}
      noFullPageHeader
      contentClassName="!pb-0 sm:!px-xl"
      backdrop="static"
      keyboard={false}
      backButton={
        !!remainingFailedPayments.length ||
        (isHomeownerMultiHome && selectedHomeKey !== null)
      }
      onBackButtonClick={
        remainingFailedPayments.length ? handleHide : handleBackToHomeList
      }
    >
      <div
        ref={modalContentRef}
        className={clsx(
          'pb-2xl pt-xl',
          !showSelectHomeStep && 'sm:pb-0',
          audience.key === 'homeowner' && 'sm:pt-2xl',
        )}
      >
        {stepContent}
      </div>
    </ModalV2>
  );
}
